// src/components/CollectionTypeBadge.jsx
//
// Badge visuel pour le type de collection.
//
// Deux modes strictement séparés — jamais mélangés :
//   - isFoundation=false (défaut) : private / inalienable / deposit
//   - isFoundation=true           : foundation_owned / foundation_founder /
//                                   foundation_donation / foundation_deposit
//
// Usage :
//   <CollectionTypeBadge type="private" />
//   <CollectionTypeBadge type="inalienable" size="small" />
//   <CollectionTypeBadge type="foundation_owned" isFoundation />
//   <CollectionTypeBadge type="foundation_deposit" isFoundation withLabel={false} />
//
import React from "react";
import { useTranslation } from "react-i18next";

// ─── Styles collectionneur (inchangés) ───────────────────────────────────────
const COLLECTOR_TYPE_STYLES = {
  private: {
    label: { fr: "Privée", en: "Private", de: "Privat" },
    bg: "rgba(255, 255, 255, 0.95)",
    color: "#2D2D2D",
    border: "1px solid #E5E2DD",
  },
  inalienable: {
    label: { fr: "Inaliénable", en: "Inalienable", de: "Unveräußerlich" },
    bg: "#C9A961",
    color: "#FFFFFF",
    border: "1px solid #C9A961",
  },
  deposit: {
    label: { fr: "Dépôt", en: "On deposit", de: "Hinterlegung" },
    bg: "#1A1A1A",
    color: "#FFFFFF",
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
};

// ─── Styles Fondation ─────────────────────────────────────────────────────────
// Palette distincte : vert forêt / brun terre / bleu nuit / or
// Jamais exposés à un collectionneur lambda
const FOUNDATION_TYPE_STYLES = {
  foundation_owned: {
    label: {
      fr: "Propriété propre",
      en: "Foundation property",
      de: "Eigentum der Stiftung",
    },
    bg: "#EBF3EF",
    color: "#2C5F4A",
    border: "1px solid #B8D9CB",
  },
  foundation_founder: {
    label: {
      fr: "Prêt fondateur",
      en: "Founder loan",
      de: "Gründerleihgabe",
    },
    bg: "#FAF8F3",
    color: "#7A5C00",
    border: "1px solid #E8D9B0",
  },
  foundation_donation: {
    label: {
      fr: "Donation / legs",
      en: "Donation / bequest",
      de: "Schenkung / Vermächtnis",
    },
    bg: "#F5ECE5",
    color: "#8B4513",
    border: "1px solid #D9B89A",
  },
  foundation_deposit: {
    label: {
      fr: "Dépôt institution",
      en: "Institutional deposit",
      de: "Institutionelles Depot",
    },
    bg: "#E5EBF3",
    color: "#1C3A5E",
    border: "1px solid #A8BDD9",
  },
};

const SIZES = {
  small:  { padding: "3px 9px",   fontSize: 9,  letterSpacing: "1px"   },
  medium: { padding: "5px 12px",  fontSize: 10, letterSpacing: "1.5px" },
  large:  { padding: "7px 16px",  fontSize: 11, letterSpacing: "1.8px" },
};

export default function CollectionTypeBadge({
  type = "private",
  size = "medium",
  withLabel = true,
  isFoundation = false,
  style = {},
  className = "",
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || "fr").split("-")[0];

  // Switch complet selon le mode — aucun croisement possible
  const styleMap = isFoundation ? FOUNDATION_TYPE_STYLES : COLLECTOR_TYPE_STYLES;
  const fallback = isFoundation
    ? FOUNDATION_TYPE_STYLES.foundation_owned
    : COLLECTOR_TYPE_STYLES.private;

  const typeStyle = styleMap[type] || fallback;
  const sizeStyle = SIZES[size] || SIZES.medium;
  const label = t(`round36.collectionTypeBadge.types.${type}`, {
    defaultValue: typeStyle.label[lang] || typeStyle.label.fr,
  });

  return (
    <span
      className={className}
      style={{
        display: "inline-block",
        backgroundColor: typeStyle.bg,
        color: typeStyle.color,
        border: typeStyle.border,
        textTransform: "uppercase",
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontWeight: 500,
        whiteSpace: "nowrap",
        ...sizeStyle,
        ...style,
      }}
    >
      {withLabel ? label : "•"}
    </span>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Retourne true si on doit afficher un badge
// Collectionneur : tout sauf private
// Fondation : tout sauf foundation_owned (équivalent du "private" pour eux)
export function shouldShowBadge(type, isFoundation = false) {
  if (isFoundation) {
    return type !== "foundation_owned";
  }
  return type === "inalienable" || type === "deposit";
}

// Retourne le label lisible d'un type
export function getCollectionTypeLabel(type, lang = "fr", isFoundation = false) {
  const styleMap = isFoundation ? FOUNDATION_TYPE_STYLES : COLLECTOR_TYPE_STYLES;
  const fallback = isFoundation
    ? FOUNDATION_TYPE_STYLES.foundation_owned
    : COLLECTOR_TYPE_STYLES.private;
  const typeStyle = styleMap[type] || fallback;
  return typeStyle.label[lang] || typeStyle.label.fr;
}

// Retourne true si le type appartient au contexte Fondation
// Utile pour détecter automatiquement isFoundation depuis le type seul
export function isFoundationType(type) {
  return type?.startsWith("foundation_");
}